/**
 * Audit Log Data Layer
 *
 * React Query hooks and types for the superadmin audit log page.
 *
 * @module superadmin/useAuditLogData
 * @see docs/action-audit-log.md
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../config';

export type AuditLogRecord = {
  id: string;
  orgId: string;
  agentType: string;
  sessionId: string;
  workflowId: string | null;
  runId: string | null;
  runStepId: string | null;
  nodeId: string | null;
  source: 'browser' | 'api';
  actionType: string;
  toolName: string | null;
  actionIndex: number | null;
  pageUrl: string | null;
  elementDescription: string | null;
  elementSelectors: unknown;
  actionArgs: unknown;
  httpMethod: string | null;
  requestUrl: string | null;
  responseStatusCode: number | null;
  success: boolean | null;
  errorMessage: string | null;
  durationMs: number | null;
  credentialId: string | null;
  screenshotS3Key: string | null;
  agentAssumptions: string[] | null;
  agentConditionReasoning: string | null;
  agentFailureContext: {
    observedBehavior?: string;
    expectedBehavior?: string;
    reproSteps?: string[];
    questions?: string[];
  } | null;
  agentSuggestedInstructions: string | null;
  timestamp: string;
  createdAt: string;
};

export type AuditLogFilters = {
  orgId?: string;
  workflowId?: string;
  runId?: string;
  agentType?: string;
  source?: string;
  actionType?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
};

type AuditLogResponse = {
  ok: boolean;
  data: AuditLogRecord[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  error?: string;
};

export function useAuditLogQuery(filters: AuditLogFilters) {
  const hasFilter = Boolean(
    filters.orgId ||
    filters.workflowId ||
    filters.runId ||
    filters.agentType ||
    filters.source ||
    filters.actionType ||
    filters.from ||
    filters.to
  );

  return useQuery({
    queryKey: ['superadmin-audit-log', filters],
    queryFn: async (): Promise<AuditLogResponse> => {
      const params = new URLSearchParams();
      if (filters.orgId) params.set('orgId', filters.orgId);
      if (filters.workflowId) params.set('workflowId', filters.workflowId);
      if (filters.runId) params.set('runId', filters.runId);
      if (filters.agentType) params.set('agentType', filters.agentType);
      if (filters.source) params.set('source', filters.source);
      if (filters.actionType) params.set('actionType', filters.actionType);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      if (filters.page) params.set('page', filters.page.toString());
      if (filters.pageSize) params.set('pageSize', filters.pageSize.toString());

      const res = await fetch(`${API_URL}/superadmin/audit-log?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('Access denied. Superadmin privileges required.');
        }
        throw new Error('Failed to fetch audit log');
      }
      return res.json();
    },
    enabled: hasFilter,
  });
}

export function formatDurationMs(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
